'use client';

import { useMemo } from 'react';
import { Box, Chip, Divider, Paper, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';

type CvmFundRow = {
  idRegistroFundo: string;
  cnpjFundo: string | null;
  codigoCvm: number | null;
  denominacaoSocial: string | null;
  tipoFundo: string | null;
  situacao: string | null;
  administrador: string | null;
  gestor: string | null;
  patrimonioLiquido: string | null;
  dataPatrimonioLiquido: string | null;
  dataRegistro: string | null;
};

type CvmFundClassRow = {
  idRegistroClasse: string;
  idRegistroFundo: string;
  cnpjClasse: string | null;
  codigoCvm: number | null;
  denominacaoSocial: string | null;
  tipoClasse: string | null;
  classificacao: string | null;
  situacao: string | null;
  publicoAlvo: string | null;
  patrimonioLiquido: string | null;
  dataPatrimonioLiquido: string | null;
};

type CvmFundSubclassRow = {
  idRegistroClasse: string;
  idSubclasse: string;
  codigoCvm: number | null;
  denominacaoSocial: string | null;
  situacao: string | null;
  publicoAlvo: string | null;
  exclusivo: string | null;
  previdenciario: string | null;
};

type TickerCvmTabClientProps = {
  cnpj: string;
  fundRows: CvmFundRow[];
  classRows: CvmFundClassRow[];
  subclassRows: CvmFundSubclassRow[];
};

const formatDate = (value: string | null) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short' }).format(date);
};

const InfoRow = ({ label, value }: { label: string; value?: string | null }) => (
  <Stack direction='row' spacing={1} alignItems='baseline'>
    <Typography variant='caption' color='text.secondary' sx={{ minWidth: 120 }}>
      {label}
    </Typography>
    <Typography variant='body2' fontWeight={600} sx={{ wordBreak: 'break-word' }}>
      {value ?? '-'}
    </Typography>
  </Stack>
);

export function TickerCvmTabClient({ cnpj, fundRows, classRows, subclassRows }: TickerCvmTabClientProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const subclassByClass = useMemo(() => {
    const map = new Map<string, CvmFundSubclassRow[]>();
    subclassRows.forEach((row) => {
      const current = map.get(row.idRegistroClasse) ?? [];
      current.push(row);
      map.set(row.idRegistroClasse, current);
    });
    return map;
  }, [subclassRows]);

  const fund = fundRows[0];

  return (
    <Stack spacing={3} sx={{ mt: 2 }}>
      {fund ? (
        <Paper variant='outlined' sx={{ p: 2 }}>
          <Stack spacing={1.5}>
            <Typography variant='h6' fontWeight={700}>
              Cadastro do fundo (CNPJ {cnpj})
            </Typography>
            <Divider />
            <Stack spacing={1}>
              <InfoRow label='Registro' value={fund.idRegistroFundo} />
              <InfoRow label='Denominacao' value={fund.denominacaoSocial} />
              <InfoRow label='Tipo' value={fund.tipoFundo} />
              <InfoRow label='Situacao' value={fund.situacao} />
              <InfoRow label='Administrador' value={fund.administrador} />
              <InfoRow label='Gestor' value={fund.gestor} />
              <InfoRow label='Patrimonio liquido' value={fund.patrimonioLiquido} />
              <InfoRow label='Data PL' value={formatDate(fund.dataPatrimonioLiquido)} />
              <InfoRow label='Data registro' value={formatDate(fund.dataRegistro)} />
            </Stack>
          </Stack>
        </Paper>
      ) : null}

      <Paper variant='outlined' sx={{ p: 2 }}>
        <Stack spacing={1.5}>
          <Typography variant='h6' fontWeight={700}>
            Classes e subclasses
          </Typography>
          <Divider />

          {classRows.length === 0 ? (
            <Typography variant='body2' color='text.secondary'>
              Nenhuma classe encontrada para este fundo.
            </Typography>
          ) : (
            <Stack spacing={2}>
              {classRows.map((classe) => {
                const subclasses = subclassByClass.get(classe.idRegistroClasse) ?? [];
                return (
                  <Paper
                    key={classe.idRegistroClasse}
                    variant='outlined'
                    sx={{
                      p: 2,
                      bgcolor: 'hsl(var(--card))',
                    }}
                  >
                    <Stack spacing={1.5}>
                      <Stack
                        direction={isMobile ? 'column' : 'row'}
                        spacing={1.5}
                        justifyContent='space-between'
                        alignItems={isMobile ? 'flex-start' : 'center'}
                      >
                        <Box>
                          <Typography variant='subtitle1' fontWeight={700}>
                            {classe.denominacaoSocial ?? 'Classe sem nome'}
                          </Typography>
                          <Typography variant='caption' color='text.secondary'>
                            Registro {classe.idRegistroClasse}
                          </Typography>
                        </Box>
                        <Stack direction='row' spacing={1} flexWrap='wrap'>
                          {classe.tipoClasse ? <Chip size='small' label={classe.tipoClasse} /> : null}
                          {classe.classificacao ? <Chip size='small' label={classe.classificacao} /> : null}
                          {classe.situacao ? <Chip size='small' label={classe.situacao} /> : null}
                        </Stack>
                      </Stack>

                      <Stack direction={isMobile ? 'column' : 'row'} spacing={2} flexWrap='wrap'>
                        <InfoRow label='CNPJ' value={classe.cnpjClasse} />
                        <InfoRow label='Publico alvo' value={classe.publicoAlvo} />
                        <InfoRow label='Patrimonio liquido' value={classe.patrimonioLiquido} />
                        <InfoRow label='Data PL' value={formatDate(classe.dataPatrimonioLiquido)} />
                      </Stack>

                      {subclasses.length > 0 ? (
                        <Stack spacing={1.5}>
                          <Divider />
                          <Typography variant='subtitle2' fontWeight={700}>
                            Subclasses
                          </Typography>
                          <Stack spacing={1}>
                            {subclasses.map((sub) => (
                              <Paper
                                key={`${classe.idRegistroClasse}-${sub.idSubclasse}`}
                                variant='outlined'
                                sx={{ p: 1.5 }}
                              >
                                <Stack
                                  direction={isMobile ? 'column' : 'row'}
                                  spacing={1.5}
                                  justifyContent='space-between'
                                  alignItems={isMobile ? 'flex-start' : 'center'}
                                >
                                  <Box>
                                    <Typography variant='subtitle2' fontWeight={700}>
                                      {sub.denominacaoSocial ?? 'Subclasse sem nome'}
                                    </Typography>
                                    <Typography variant='caption' color='text.secondary'>
                                      Subclasse {sub.idSubclasse}
                                    </Typography>
                                  </Box>
                                  <Stack direction='row' spacing={1} flexWrap='wrap'>
                                    {sub.situacao ? <Chip size='small' label={sub.situacao} /> : null}
                                    {sub.publicoAlvo ? <Chip size='small' label={sub.publicoAlvo} /> : null}
                                    {sub.exclusivo ? <Chip size='small' label={`Exclusivo: ${sub.exclusivo}`} /> : null}
                                    {sub.previdenciario ? (
                                      <Chip size='small' label={`Previdenciario: ${sub.previdenciario}`} />
                                    ) : null}
                                  </Stack>
                                </Stack>
                              </Paper>
                            ))}
                          </Stack>
                        </Stack>
                      ) : (
                        <Typography variant='body2' color='text.secondary'>
                          Nenhuma subclasse cadastrada para esta classe.
                        </Typography>
                      )}
                    </Stack>
                  </Paper>
                );
              })}
            </Stack>
          )}
        </Stack>
      </Paper>
    </Stack>
  );
}
